import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { setUserStandby } from 'src/redux/slices/user';
import { useSelector, useDispatch } from 'src/redux/store';
import { verifyRules, hasFeatureAccess, FEATURE_KEYS } from 'src/utils/functions';
import Logo from 'src/components/Logo';
import useAuth from 'src/hooks/useAuth';
import NotificationsPopover from './NotificationsPopover';
import Iconify from 'src/components/Iconify';
import { setChatInfo } from 'src/redux/slices/chat';
import { setMessages } from 'src/redux/slices/staff';
import Label from 'src/components/Label';
import v2_actionEditarUsuario from 'src/actions/v2/usuarios/v2_actionEditarUsuario';
import { DropdownMenuDemo2 } from 'src/@/components/use/dropdownMenu/DropdownMenu2';
import { DropdownAccountNav } from 'src/@/components/use/dropdownMenu/DropdownAccountNav';
import { ZuiTooltip } from 'src/@/components/use/tooltip';
import { HelpComponent } from 'src/sections/@global/help';
import ContactsPopover from './ContactsPopover';
import { InternetConnectionAlertHeader } from 'src/sections/@global/connection/InternetConnectionAlertHeader';
import { getSpent } from 'src/helpers/cliente.helper';
import { LanguagePopover } from 'src/@/components/use/LanguagePopover';
import { useSomNotificacao, usePermissaoNotificacao } from 'src/hooks/useNotificacoes';
import { Switch } from 'src/@/components/ui/switch';

DashboardNavbar.propTypes = {
    onOpenSidebar: PropTypes.func
};

export default function DashboardNavbar({ topOffset = 0 }) {
    const { user, cliente } = useAuth();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation('navigation');
    const { t: tNotif } = useTranslation('notifications');
    const { roomsWithNewMessages } = useSelector((state) => state.staff);
    const { isUserStandby } = useSelector((state) => state.user);
    const { flag } = useSelector((state) => state.global);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    // Ver comentario em MainSidebar: o toggle deixou de recarregar a pagina e
    // agora fica sincronizado entre as telas via hook.
    const { ligado: somLigado, alternar: alternarSom } = useSomNotificacao();
    const { estado: permissaoNotificacao, pedir: pedirPermissaoNotificacao } = usePermissaoNotificacao();
    const [saldo, setSaldo] = useState(0);
    const [credit, setCredit] = useState(0);
    const [isLoadingSaldo, setIsLoadingSaldo] = useState(false);
    let location = useLocation();
    let path = location.pathname.substring(1); // Remove o "/" inicial

    // Recebe o boolean direto (padrão onCheckedChange do Switch Radix), sem o
    // event sintético que o Switch do MUI passava.
    async function handleEditStandbyUser(check) {
        dispatch(setUserStandby(check));
        let data = {
            action: "update_standby_users",
            scope: { userStandby: check }
        }
        await v2_actionEditarUsuario(user.id, data)
    }

    async function handleGetSaldo() {
        try {
            setIsLoadingSaldo(true);
            const hoje = new Date();
            const startDate = hoje.toISOString().split('T')[0];
            const endDate = hoje.toISOString().split('T')[0];
            const querys = `?inicio=${startDate}&final=${endDate}&periodo=mes_atual`;
            const response = await getSpent(user.clienteId, querys);
            // Calcula o saldo disponível (33% do limite restante)
            const limiteRestante = response?.data?.credits?.total?.limit - response?.data?.credits?.total?.use || 0;
            const saldoDisponivel = limiteRestante * 0.33;
            setSaldo(saldoDisponivel);
            setCredit(limiteRestante);
        } catch (error) {
            console.error('Erro ao buscar saldo:', error);
        } finally {
            setIsLoadingSaldo(false);
        }
    }

    useEffect(() => {
        handleGetSaldo();
    }, []);

    const atualizarSaldo = () => {
        handleGetSaldo();
    };

    const NavButton = ({ nome, slug, to, badge, icon, isMobile = false }) => {
        const isActive = path.includes(slug);
        return (
            <button onClick={() => { to(); if (isMobile) setIsMobileMenuOpen(false); }} className={`flex items-center px-2 py-1.5 rounded-lg transition-all duration-200 text-sm ${isActive ? 'bg-[#7C3AED]/10 text-[#7C3AED]' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'} ${isMobile ? 'w-full justify-start' : 'justify-center'}`} >
                <div className="flex items-center gap-2">
                    {/* <Iconify icon={icon} sx={{ width: 18, height: 18, color: isActive ? '#7C3AED' : 'inherit'}} /> */}
                    <span className={`whitespace-nowrap ${isMobile ? 'block' : 'hidden lg:block'}`}>
                        {nome}
                    </span>
                    {badge > 0 && (
                        <span className={`relative inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold text-white leading-none ring-2 ring-white shadow-md ${isActive ? 'bg-gradient-to-br from-[#8B5CF6] to-[#7C3AED] shadow-[#7C3AED]/40' : 'bg-gradient-to-br from-[#F87171] to-[#EF4444] shadow-red-500/40'}`}>
                            <span className={`absolute inset-0 rounded-full animate-ping opacity-40 ${isActive ? 'bg-[#7C3AED]' : 'bg-[#EF4444]'}`} />
                            <span className="relative">{badge > 99 ? '99+' : badge}</span>
                        </span>
                    )}
                </div>
            </button>
        );
    };

    const NavigationItems = ({ isMobile = false }) => (
        <div className={`flex ${isMobile ? 'flex-col space-y-1' : 'flex-row space-x-1'}`}>
            {verifyRules(user.rules, "chats") && (
                <NavButton nome={t('sidebar.conversations')} slug="chats/chat" to={() => { navigate('/chats'); dispatch(setChatInfo(null)); dispatch(setMessages([])); }} badge={0} icon="ph:chats-circle-duotone" isMobile={isMobile} />
            )}
            {hasFeatureAccess(cliente, user, FEATURE_KEYS.CHAT_ARCHIVE) && (
                <NavButton nome="Arquivadas" slug="chats/arquivadas" to={() => navigate('/chats/arquivadas')} badge={0} icon="eva:archive-outline" isMobile={isMobile} />
            )}
            {verifyRules(user.rules, "chat_interno") && (
                <NavButton nome={t('sidebar.internalChat')} slug="chats/interno" to={() => navigate('/chats/interno')} badge={roomsWithNewMessages.length} icon="iconoir:group" isMobile={isMobile} />
            )}
            {verifyRules(user.rules, "chamados") && (
                <NavButton nome={t('sidebar.funnel')} slug="dashboard/negocios" to={() => navigate('/dashboard/negocios')} badge={0} icon="iconamoon:funnel-thin" isMobile={isMobile} />
            )}
            <NavButton nome={t('sidebar.contacts')} slug="dashboard/contatos" to={() => navigate('/dashboard/contatos')} badge={0} icon="ph:address-book" isMobile={isMobile} />
            {verifyRules(user.rules, "chamados") && (
                <NavButton nome={t('sidebar.tickets')} slug="dashboard/chamados" to={() => navigate('/dashboard/chamados')} badge={0} icon="clarity:tasks-line" isMobile={isMobile} />
            )}
                <NavButton nome={t('sidebar.tarefas')} slug="dashboard/tarefas" to={() => navigate('/dashboard/tarefas')} badge={0} icon="ph:task" isMobile={isMobile} />
                {hasFeatureAccess(cliente, user, FEATURE_KEYS.PROPOSALS) && (
                    <NavButton nome={t('sidebar.propostas')} slug="dashboard/propostas" to={() => navigate('/dashboard/propostas')} badge={0} icon="ph:file-text" isMobile={isMobile} />
                )}

        </div>
    );

    return (
        <header
            className="fixed z-10 w-full border-b border-gray-100 bg-white backdrop-blur-sm transition-all duration-300"
            style={{ top: topOffset }}
        >
            {flag?.status && (
                <div className={`flex w-full bg-${flag?.flag}-600 h-8 items-center justify-center text-xs font-medium text-white px-4`}>
                    {flag?.msg}
                </div>
            )}
            <div className="flex h-14 items-center px-4 w-full justify-between">
                {/* Logo & Mobile Menu & Nav */}
                <div className="flex items-center gap-4">
                    <div className='flex items-center gap-4'>
                        <Logo />
                        <div className="h-6 w-[1px] bg-gray-200 hidden md:block" />
                    </div>
                    <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="md:hidden p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors">
                        <Iconify icon={isMobileMenuOpen ? "heroicons:x-mark" : "heroicons:bars-3"} width={24} />
                    </button>
                    <div className="hidden md:flex">
                        <NavigationItems />
                    </div>
                    <div className="hidden md:block">
                        <DropdownMenuDemo2 />
                    </div>
                </div>
                {/* Right Actions */}
                <div className="flex items-center gap-2 md:gap-3">
                    <InternetConnectionAlertHeader />
                    <ZuiTooltip content={
                        <div className="space-y-1 p-1">
                            <div className="font-semibold text-xs text-gray-900">{t('navbar.credits')}</div>
                            <div className="text-[10px] text-gray-500 space-y-0.5">
                                <div>{t('navbar.balance')}: <span className="font-medium text-gray-700">{credit}</span> {t('navbar.credits').toLowerCase()}</div>
                                <div>{t('navbar.status')}: <span className={saldo < 0 ? 'text-red-500' : 'text-green-600'}>{saldo < 0 ? t('common:status.negative') : t('common:status.positive')}</span></div>
                            </div>
                        </div>
                    }
                    >
                        <div className={`flex items-center gap-2 px-3 py-1 rounded-lg border transition-all duration-200 ${saldo < 0 ? 'bg-red-50 border-red-100 text-red-600' : 'bg-indigo-50 border-indigo-100 text-indigo-600 hover:bg-indigo-100'}`}>
                            <button onClick={() => navigate('/dashboard/conta/consumo')} className="flex items-center gap-1.5">
                                <Iconify icon="solar:wallet-bold-duotone" width={16} />
                                <span className="text-xs font-semibold">{credit}</span>
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); atualizarSaldo(); }} className={`p-0.5 rounded-full hover:bg-black/5 disabled:opacity-50`} disabled={isLoadingSaldo} >
                                <Iconify icon="lucide:refresh-cw" width={12} className={isLoadingSaldo ? 'animate-spin' : ''} />
                            </button>
                        </div>
                    </ZuiTooltip>
                    {/* <ZuiTooltip content={
            <div className="space-y-1 p-1 bg-slate-50">
              <div className="font-semibold text-xs text-gray-900">Status de Disponibilidade</div>
              <div className="text-[10px] text-gray-500">
                {isUserStandby ? "Você está ausente e não receberá novos chats." : "Você está online e disponível para atendimentos."}
              </div>
              <div>
                Clique no icone para alterar o status
              </div>
            </div>
          }
          >
            <button onClick={() => handleEditStandbyUser(!isUserStandby)} className={`hidden md:flex items-center gap-2 p-1 rounded-sm border transition-all duration-300 ${isUserStandby ? 'bg-red-50 hover:bg-red-100' : 'bg-green-50 hover:bg-green-100'}`} >
              <Iconify icon={"fluent:person-support-20-filled"} className={`${isUserStandby ? 'text-red-400' : 'text-green-400'} w-[20px] h-[20px]`} />
            </button>
          </ZuiTooltip> */}
                    <div className="h-6 w-[1px] bg-gray-200 hidden md:block" />
                    <div className="flex items-center gap-1">
                        <button
                            type="button"
                            onClick={() => alternarSom()}
                            className="flex h-9 w-9 items-center justify-center text-gray-500 hover:bg-gray-100 hover:text-gray-900 rounded-lg transition-colors"
                        >
                            <Iconify icon={somLigado ? "solar:volume-loud-bold-duotone" : "solar:volume-cross-bold-duotone"} width={20} />
                        </button>
                        {permissaoNotificacao !== 'granted' && permissaoNotificacao !== 'unsupported' && (
                            <button
                                type="button"
                                onClick={() => pedirPermissaoNotificacao()}
                                title={tNotif('permissao.desativadas')}
                                className="flex h-9 w-9 items-center justify-center text-amber-500 hover:bg-amber-50 rounded-lg transition-colors"
                            >
                                <Iconify icon="eva:bell-off-outline" width={20} />
                            </button>
                        )}
                        {/* <ContactsPopover /> */}
                        <NotificationsPopover />
                        <HelpComponent />
                        <LanguagePopover />
                    </div>
                    <div className="h-6 w-[1px] bg-gray-200 hidden md:block" />
                    <DropdownAccountNav />
                </div>
            </div>
            {isMobileMenuOpen && (
                <div className="md:hidden absolute top-14 left-0 w-full bg-white border-b border-gray-100 shadow-xl z-40 animate-in slide-in-from-top-2 duration-200">
                    <div className="p-4 space-y-4">
                        <div className="space-y-1">
                            <p className="pl-2 text-xs font-semibold uppercase tracking-wider text-gray-400">{t('common:menu')}</p>
                            <NavigationItems isMobile={true} />
                        </div>
                        <div className="h-px bg-gray-100" />
                        <div className={`rounded-xl p-3 border ${isUserStandby ? 'bg-amber-50 border-amber-100' : 'bg-green-50 border-green-100'}`}>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className={`p-2 rounded-full ${isUserStandby ? 'bg-amber-100 text-amber-600' : 'bg-green-100 text-green-600'}`}>
                                        <Iconify icon={isUserStandby ? "solar:coffee-bold-duotone" : "solar:user-check-bold-duotone"} width={20} />
                                    </div>
                                    <div>
                                        <p className={`text-sm font-semibold ${isUserStandby ? 'text-amber-900' : 'text-green-900'}`}>
                                            {isUserStandby ? t('account.awayMode') : t('account.onlineMode')}
                                        </p>
                                        <p className={`text-xs ${isUserStandby ? 'text-amber-700' : 'text-green-700'}`}>
                                            {isUserStandby ? t('account.awayInfo') : t('account.onlineInfo')}
                                        </p>
                                    </div>
                                </div>
                                <Switch
                                    checked={isUserStandby}
                                    onCheckedChange={handleEditStandbyUser}
                                    // Replica o color warning/success do Switch antigo do MUI
                                    className={isUserStandby ? 'data-[state=checked]:bg-amber-500' : 'data-[state=checked]:bg-green-500'}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
}